import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Layout,
  Menu,
  Switch,
  Space,
  Dropdown,
  Avatar,
  Typography,
} from "antd";
import {
  DashboardOutlined,
  AppstoreOutlined,
  TeamOutlined,
  KeyOutlined,
  MobileOutlined,
  BarChartOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  CloudUploadOutlined,
  TagsOutlined,
  MoonOutlined,
  SunOutlined,
  FileProtectOutlined,
  UserOutlined,
  LogoutOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { DetailModalManager } from "@/components/detail-views";

const { Header, Sider, Content } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

const menuItems: MenuItem[] = [
  { key: "/", icon: <DashboardOutlined />, label: "总览" },
  { key: "/projects", icon: <AppstoreOutlined />, label: "项目管理" },
  { key: "/features", icon: <TagsOutlined />, label: "功能管理" },
  { key: "/products", icon: <AppstoreOutlined />, label: "产品管理" },
  { key: "/versions", icon: <FileProtectOutlined />, label: "版本管理" },
  { key: "/customers", icon: <TeamOutlined />, label: "客户管理" },
  { key: "/issues", icon: <KeyOutlined />, label: "授权签发" },
  { key: "/activations", icon: <MobileOutlined />, label: "激活记录" },
  { key: "/statistics", icon: <BarChartOutlined />, label: "统计分析" },
  { key: "/installations", icon: <DesktopOutlined />, label: "安装记录" },
  { key: "/settings", icon: <SettingOutlined />, label: "系统设置" },
  { key: "/backup", icon: <CloudUploadOutlined />, label: "备份管理" },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { user, handleLogout } = useAuth();

  const activeKey = (() => {
    const path = location.pathname;
    if (path === "/") return "/";
    return "/" + path.split("/").filter(Boolean)[0];
  })();

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
  };

  const onUserMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      handleLogout();
    } else if (key === "settings") {
      navigate("/settings");
    }
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "user-info",
      label: (
        <div style={{ padding: "4px 8px", cursor: "default" }}>
          <Typography.Text strong>
            {user?.nickname || user?.username || "用户"}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            角色: {user?.role || "-"}
          </Typography.Text>
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "系统设置",
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      danger: true,
    },
  ];

  return (
    <Layout style={{ height: "100vh" }}>
      <Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px",
        }}
      >
        <Space>
          <SafetyCertificateOutlined
            style={{ fontSize: 24, color: "#fff", marginRight: 12 }}
          />
          <span style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>
            授权管理系统
          </span>
        </Space>
        <Space size="middle">
          <span style={{ color: "#fff", marginRight: 8 }}>
            {isDark ? <MoonOutlined /> : <SunOutlined />}
          </span>
          <Switch
            checked={isDark}
            onChange={toggleTheme}
            checkedChildren="暗黑"
            unCheckedChildren="明亮"
          />
          <Dropdown
            menu={{ items: userMenuItems, onClick: onUserMenuClick }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Space
              style={{
                cursor: "pointer",
                color: "#fff",
                padding: "0 8px",
                borderRadius: 4,
              }}
            >
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ backgroundColor: isDark ? "#1a1a1a" : "#87d068" }}
              />
              <span>{user?.username || "用户"}</span>
            </Space>
          </Dropdown>
        </Space>
      </Header>
      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme={isDark ? "dark" : "light"}
          width={220}
        >
          <Menu
            mode="inline"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={onMenuClick}
            style={{ height: "100%", borderRight: 0 }}
          />
        </Sider>
        <Content
          style={{
            padding: 24,
            overflow: "auto",
            background: isDark ? "#141414" : "#f5f5f5",
          }}
        >
          <Outlet />
          <DetailModalManager />
        </Content>
      </Layout>
    </Layout>
  );
}
